/**
 * useAgentUpdateStatus – estado de la actualización remota de un agente.
 *
 * El POST que encola la actualización no basta para la ficha, porque devuelve
 * «encolado» y ahí se acaba la información. Hacen falta tres cosas más:
 *
 *   1. saber que sigue en curso mientras el agente recoge el comando (su
 *      sondeo puede tardar),
 *   2. no cantar «actualizado» cuando el agente solo ha *preparado* el
 *      instalador: el reinicio del servicio ocurre 8 s después,
 *   3. enterarse sola cuando el latido posterior confirma la versión nueva.
 *
 * El backend (`GET /agent/update-status`) concentra esa lógica; este hook solo
 * la refresca mientras la actualización está viva.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const POLL_MS = 10000;
// Cubre el sondeo del agente + la instalación silenciosa + el arranque del
// servicio. Pasado ese tiempo se deja de preguntar: la ficha sigue mostrando
// el último estado conocido.
const MAX_WAIT_MS = 30 * 60 * 1000;

const TERMINAL_STATES = new Set(['updated', 'up-to-date', 'failed', 'idle']);

/** Presentación del estado: etiqueta, color y tooltip. */
export function describeAgentUpdate(status) {
  if (!status || !status.state || status.state === 'idle') return null;
  const current = status.current_version || '?';
  const target = status.target_version || '?';
  switch (status.state) {
    case 'updating':
      return {
        label: 'Actualizando…',
        color: 'var(--amber-500, #f59e0b)',
        title: status.message || `Objetivo ${target}. El agente recogerá el comando en su próximo sondeo.`,
      };
    case 'updated':
      return {
        label: `Actualizado ${current}`,
        color: 'var(--green-600, #16a34a)',
        title: status.message || `El agente ya reporta la versión ${current}.`,
      };
    case 'up-to-date':
      return {
        label: 'Agente al día',
        color: 'var(--text-muted, #6b7280)',
        title: status.message || `Ya estaba en ${current}; no se reinstaló nada.`,
      };
    case 'unconfirmed':
      return {
        label: 'Sin confirmar',
        color: 'var(--amber-500, #f59e0b)',
        title:
          status.message
          || `Instalador preparado (objetivo ${target}); el agente aún reporta ${current}.`,
      };
    case 'failed':
      return {
        label: 'Actualización fallida',
        color: 'var(--red-600, #dc2626)',
        title: status.message || 'El agente no pudo actualizar.',
      };
    default:
      return null;
  }
}

export default function useAgentUpdateStatus(
  companyId,
  { ciType, ciId, agentId, enabled = true } = {},
) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const activeSinceRef = useRef(0);
  const canQuery = Boolean(
    enabled && companyId && (agentId || (ciType && ciId)),
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!canQuery) return null;
    setLoading(true);
    try {
      const next = await api.getAgentUpdateStatus(companyId, {
        ci_type: ciType,
        ci_id: ciId,
        agent_id: agentId,
      });
      if (!mountedRef.current) return next;
      setStatus(next);
      setError(null);
      if (next?.state === 'updating' && !activeSinceRef.current) {
        activeSinceRef.current = Date.now();
      }
      if (TERMINAL_STATES.has(next?.state)) {
        activeSinceRef.current = 0;
      }
      return next;
    } catch (e) {
      if (mountedRef.current) {
        setError(e?.message || 'No se pudo consultar el estado de la actualización');
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [canQuery, companyId, ciType, ciId, agentId]);

  // Foto al abrir la ficha (y al cambiar de CI).
  useEffect(() => {
    activeSinceRef.current = 0;
    if (canQuery) {
      refresh();
    } else {
      setStatus(null);
    }
  }, [canQuery, refresh]);

  const isActive = status?.state === 'updating' || status?.state === 'unconfirmed';

  // Sondeo mientras la actualización está viva.
  useEffect(() => {
    if (!canQuery || !isActive) return undefined;
    if (!activeSinceRef.current) activeSinceRef.current = Date.now();
    const timer = window.setInterval(() => {
      if (
        activeSinceRef.current
        && Date.now() - activeSinceRef.current > MAX_WAIT_MS
      ) {
        window.clearInterval(timer);
        return;
      }
      refresh();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [canQuery, isActive, refresh]);

  /** Marca el estado como «actualizando» al encolar, sin esperar al sondeo. */
  const markEnqueued = useCallback(() => {
    activeSinceRef.current = Date.now();
    setStatus((prev) => ({
      ...(prev || {}),
      state: 'updating',
      message: 'Comando encolado; el agente lo ejecutará en su próximo sondeo.',
    }));
    return refresh();
  }, [refresh]);

  return { status, loading, error, refresh, markEnqueued };
}
